import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { FormOwnershipSettings, normalizeFormOwnershipSettings } from 'common/lib/model/form/FormOwnership';
import { FormOwnershipResponse, useFormOwnership } from 'src/queries/useFormOwnership';
import NavBar from 'src/containers/NavBar';
import PageContainer from 'src/components/PageContainer';
import PageContent from 'src/components/PageContent';
import Card from 'src/components/Card';
import Button from 'src/components/Button';
import LoadingIcon from 'src/components/LoadingIcon';
import { parseError } from 'src/utils';
import OwnershipRuleEditor, { DraftRule } from './OwnershipRuleEditor';
import styles from './styles.module.scss';

type Draft = { mode: FormOwnershipSettings['mode']; models: { modelName: string; rules: DraftRule[] }[] };
function draftModels(models: FormOwnershipResponse['effectiveModels']): Draft['models'] {
    return models.map((model) => ({
        ...model,
        rules: model.rules.map((rule) => ({
            ...rule,
            indexes: Array.isArray(rule.indexes) ? rule.indexes.join(', ') : rule.indexes,
        })),
    }));
}

export function OwnershipSettingsEditor({
    data,
    saving,
    onSave,
}: {
    data: FormOwnershipResponse;
    saving: boolean;
    onSave: (settings: FormOwnershipSettings) => Promise<unknown>;
}) {
    const [draft, setDraft] = useState<Draft>(() => ({
        mode: data.settings.mode,
        models: draftModels(data.effectiveModels),
    }));
    const [message, setMessage] = useState<{ error?: string; success?: string }>({});
    const update = (next: Draft) => {
        setDraft(next);
        setMessage({});
    };
    async function save() {
        try {
            const models =
                draft.mode === 'custom'
                    ? draft.models.map((model) => ({
                          ...model,
                          rules: model.rules.map((rule) => {
                              if (rule.indexes === undefined || rule.indexes === 'all') return rule;
                              if (!/^\s*\d+\s*(,\s*\d+\s*)*$/.test(rule.indexes))
                                  throw new Error('Enter indexes as positive whole numbers separated by commas.');
                              return { ...rule, indexes: rule.indexes.split(',').map(Number) };
                          }),
                      }))
                    : [];
            const settings = normalizeFormOwnershipSettings({ version: 1, mode: draft.mode, models }, data.catalog);
            await onSave(settings);
            setMessage({ success: 'My Incomplete Forms settings saved.' });
        } catch (error) {
            setMessage({
                error:
                    error instanceof Error && !('response' in error)
                        ? error.message
                        : parseError(error as Parameters<typeof parseError>[0]),
            });
        }
    }
    const availableModels = data.catalog.filter(
        (model) => !draft.models.some((item) => item.modelName === model.modelName),
    );
    return (
        <Card className={styles.card} label="My Incomplete Forms">
            <div className={styles.body}>
                <p className={styles.intro}>Choose which forms appear in a provider’s My Incomplete Forms list.</p>
                <fieldset disabled={saving}>
                    <legend className={styles.srOnly}>Form ownership</legend>
                    <div className={styles.choices}>
                        <label className={styles.choice}>
                            <input
                                type="radio"
                                name="ownershipMode"
                                value="defaults"
                                checked={draft.mode === 'defaults'}
                                onChange={() => update({ ...draft, mode: 'defaults' })}
                            />
                            <span>
                                <strong>Use default ownership</strong>
                                <span className={styles.description}>
                                    Providers see incomplete surgery forms where they appear in the anesthesia provider
                                    list. Includes every provider entry, regardless of role.
                                </span>
                            </span>
                        </label>
                        <label className={styles.choice}>
                            <input
                                type="radio"
                                name="ownershipMode"
                                value="custom"
                                checked={draft.mode === 'custom'}
                                onChange={() => update({ ...draft, mode: 'custom' })}
                            />
                            <span>
                                <strong>Customize ownership</strong>
                                <span className={styles.description}>
                                    Choose the models and properties that identify a form’s provider.
                                </span>
                            </span>
                        </label>
                    </div>
                    {draft.mode === 'disabled' && (
                        <p role="status">Ownership is currently disabled. Choose an option above to enable it.</p>
                    )}
                    {draft.mode === 'defaults' && data.effectiveModels.length === 0 && (
                        <p className={styles.help}>
                            If your organization does not use the Surgery model, customize ownership to choose another
                            model.
                        </p>
                    )}
                    {draft.mode === 'custom' && (
                        <div className={styles.custom}>
                            <p className={styles.help}>
                                A form belongs to a provider when any property below matches their claimed NPI or linked
                                provider ID.
                            </p>
                            {draft.models.map((model, modelIndex) => {
                                const definition = data.catalog.find((item) => item.modelName === model.modelName);
                                const removeModel = () =>
                                    update({ ...draft, models: draft.models.filter((_, i) => i !== modelIndex) });
                                if (!definition)
                                    return (
                                        <section key={model.modelName} className={styles.model}>
                                            <p role="alert">Model {model.modelName} is no longer available.</p>
                                            <Button text="Remove model" type="transparent" onClick={removeModel} />
                                        </section>
                                    );
                                function setRules(rules: DraftRule[]) {
                                    update({
                                        ...draft,
                                        models: draft.models.map((item, index) =>
                                            index === modelIndex ? { ...item, rules } : item,
                                        ),
                                    });
                                }
                                return (
                                    <section
                                        className={styles.model}
                                        key={model.modelName}
                                        aria-label={definition.title || model.modelName}
                                    >
                                        <div className={styles.modelHeader}>
                                            <h2>
                                                {definition.title || model.modelName} <span>{model.modelName}</span>
                                            </h2>
                                            <Button
                                                className={styles.textButton}
                                                text="Remove model"
                                                type="transparent"
                                                onClick={removeModel}
                                            />
                                        </div>
                                        <div className={styles.modelBody}>
                                            <div className={styles.tableScroll}>
                                                <table
                                                    className={styles.propertyTable}
                                                    aria-label={`${
                                                        definition.title || model.modelName
                                                    } ownership properties`}
                                                >
                                                    <colgroup>
                                                        <col />
                                                        <col className={styles.identityColumn} />
                                                        <col className={styles.indexColumn} />
                                                        <col className={styles.conditionColumn} />
                                                        <col className={styles.removeColumn} />
                                                    </colgroup>
                                                    <thead>
                                                        <tr>
                                                            <th scope="col">Property</th>
                                                            <th scope="col">Contains</th>
                                                            <th scope="col">Indexes</th>
                                                            <th scope="col">Condition</th>
                                                            <th scope="col">
                                                                <span className={styles.srOnly}>Remove</span>
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {model.rules.map((rule, index) => (
                                                            <OwnershipRuleEditor
                                                                key={`${index}-${rule.property}`}
                                                                model={definition}
                                                                rule={rule}
                                                                onChange={(next) =>
                                                                    setRules(
                                                                        model.rules.map((item, i) =>
                                                                            i === index ? next : item,
                                                                        ),
                                                                    )
                                                                }
                                                                onRemove={() =>
                                                                    setRules(model.rules.filter((_, i) => i !== index))
                                                                }
                                                            />
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <Button
                                                className={styles.textButton}
                                                text="+ Add property"
                                                type="transparent"
                                                onClick={() =>
                                                    setRules([...model.rules, { property: '', identity: 'npi' }])
                                                }
                                            />
                                        </div>
                                    </section>
                                );
                            })}
                            {availableModels.length > 0 && (
                                <label className={styles.addModel}>
                                    Add model
                                    <select
                                        value=""
                                        onChange={(event) =>
                                            update({
                                                ...draft,
                                                models: [
                                                    ...draft.models,
                                                    {
                                                        modelName: event.target.value,
                                                        rules: [{ property: '', identity: 'npi' }],
                                                    },
                                                ],
                                            })
                                        }
                                    >
                                        <option value="">Choose a model…</option>
                                        {availableModels.map((model) => (
                                            <option key={model.modelName} value={model.modelName}>
                                                {model.title || model.modelName} ({model.modelName})
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}
                        </div>
                    )}
                    <div className={styles.actions}>
                        <Button text="Save settings" type="primary" isLoading={saving} onClick={save} />
                    </div>
                </fieldset>
                {message.error && (
                    <p className={styles.error} role="alert">
                        {message.error}
                    </p>
                )}
                {message.success && <p role="status">{message.success}</p>}
            </div>
        </Card>
    );
}

export default observer(function FormOwnershipSettingsPage() {
    const { query, mutation, orgId } = useFormOwnership();
    return (
        <PageContainer>
            <NavBar />
            <PageContent>
                <h1 className={styles.title}>Form Settings</h1>
                {query.isLoading ? (
                    <LoadingIcon />
                ) : query.error || !query.data ? (
                    <div role="alert">
                        <p>Unable to load form settings.</p>
                        <Button text="Try again" onClick={() => query.refetch()} />
                    </div>
                ) : (
                    <OwnershipSettingsEditor
                        key={orgId}
                        data={query.data}
                        saving={mutation.isLoading}
                        onSave={mutation.mutateAsync}
                    />
                )}
            </PageContent>
        </PageContainer>
    );
});
